










































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Highcharts from 'highcharts';
import { sessionModule } from '@/trials/store/modules/session';
import { SysConst as SC } from '@/trials/plugins/const';

import KnowledgeChunks from '@/trials/domain/objects/knowledgeChunks';
import DataTable from '@/trials/views/components/DataTable.vue';
import NotificationDialogData from '@/trials/domain/objects/notificationDialogData';

@Component({ components: { DataTable } })
export default class KCSummary extends Vue {
    /*----------------------------------*
     * Props
     *-----------------------------------*/
    @Prop({ required: true })
    private kcList: KnowledgeChunks | null;
    @Prop({ default: false})
    private isPredict: boolean;
    @Prop({ default: 0 })
    private selectedIndex: number;

    /*----------------------------------*
     * Data
     *-----------------------------------*/
    private selected: number = 1;
    private tableDataPos: any[] = [];
    private defaultSortPos: any = {};
    private tableDataNeg: any[] = [];
    private defaultSortNeg: any = {};
    private chartSelector: string = '';
    private standby: boolean = false;
    private posColor: string = SC.Color.POSITIVE;
    private negColor: string = SC.Color.NEGATIVE;
    private showPieChart: boolean = true;

    private chartParam: { [key: string]: any } = {
        chart: {
            backgroundColor: 'transparent',
            plotBackgroundColor: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: 200,
        },
        credits: { enabled: false }, // 「Highcharts.com」非表示
        title: {
            text: '',
            align: 'center',
            verticalAlign: 'middle',
            style: { color: '#536DFE' },
            y: 20,
        },
        tooltip: {
            pointFormat: '<b>{point.percentage:.1f}%</b>',
        },
        plotOptions: {
            pie: {
                dataLabels: {
                    enabled: true,
                    distance: 0,
                    style: {
                        fontWeight: 'bold',
                        color: 'white',
                    },
                },
                startAngle: 0,
                endAngle: 0,
                center: ['50%', '50%'],
                size: '100%',
            },
        },
        series: [
            {
                type: 'pie',
                name: '全体解析',
                innerSize: '60%',
                data: [],
                turboThreshold: 2000,
            },
        ],
    };

    /*----------------------------------*
     * Computed
     *-----------------------------------*/

    /*----------------------------------*
     * ライフサイクルフック
     *-----------------------------------*/
    created() {
        this.chartSelector =
            'kc-summary-' +
            Math.random()
                .toString(32)
                .substring(2);
    }

    mounted() {
        this.reloadSummary();
    }

    /*----------------------------------*
     * Watch
     *-----------------------------------*/
    @Watch('kcList')
    private renderChild(newVal: KnowledgeChunks) {
        // 有効データ投入確認
        if (newVal) {
            this.reloadSummary();
        }
    }

    @Watch('$i18n.locale')
    private changeLocale(newVal: string) {
        this.reloadSummary();
    }

    /*----------------------------------*
     * Methods
     *-----------------------------------*/
    private reloadSummary(): void {
        this.standby = false;
        this.showPieChart = this.canDrawChart();

        Vue.nextTick(() => {
            if (this.showPieChart) {
                this.drawChart();
            }
            this.setTableData();
            this.standby = true;
        });
    }

    /**
     * 円グラフを描画可能かどうかを判定します。
     * @returns 描画可能な場合は`true`、その他の場合は`false`。
     */
    private canDrawChart(): boolean {
        if (!this.isPredict) {
            return true;
        }

        // POS/NEG ナレッジチャンクの数
        const n_pos = this.kcList.posObjects.length;
        const n_neg = this.kcList.negObjects.length;

        // 選択中の推論結果
        const prediction: string = sessionModule.getPredictResults.fetchByIndex(this.selectedIndex).predict;

        if (prediction === 'pos' && n_pos === 0) {
            sessionModule.showNotificationDialog(new NotificationDialogData('trials.caption.results.data.no_data_pos'));
            return false;
        }

        if (prediction === 'neg' && n_neg === 0) {
            sessionModule.showNotificationDialog(new NotificationDialogData('trials.caption.results.data.no_data_neg'));
            return false;
        }

        return true;
    }

    private setPieChartData(): void {
        if (this.kcList === null) {
            return;
        }

        const chartData = [];
        const posData: any[] = this.kcList.posObjects;
        const negData: any[] = this.kcList.negObjects;

        const hasCorrectedWeight = 'corrected_weight' in (posData[0] || negData[0] || {});
        const key = hasCorrectedWeight && this.isPredict ? 'corrected_weight' : 'weight';
        const picker = item => item[key];
        const criterion = (a, b) => picker(a) > picker(b) ? 1 : -1;

        // パイチャート用negData作成

        // weightの昇順でソート
        negData.sort(criterion);

        for (let i = 0; i < negData.length; i++) {
            const chunk = negData[i];
            chartData.push({
                name: chunk.chunk,
                y: Math.abs(picker(chunk)),
                color: SC.Color.NEGATIVE,
                dataLabels: {
                    enabled: false,
                },
            });
        }

        // パイチャート用posData作成

        // weightの昇順でソート
        posData.sort(criterion);
        for (let i = 0; i < posData.length; i++) {
            const chunk = posData[i];
            chartData.push({
                name: chunk.chunk,
                y: Math.abs(picker(chunk)),
                color: SC.Color.POSITIVE,
                dataLabels: {
                    enabled: false,
                },
            });
        }

        this.chartParam.series[0].data = chartData;
    }

    private drawChart(): void {
        this.setPieChartData();
        Highcharts.chart(this.chartSelector, this.chartParam);
    }

    private setTableData(): void {
        if (this.kcList === null) {
            return;
        }

        const showHeaders = [
            this.$i18n.t('trials.caption.results.knowledge_chunk.chunk'),
            this.$i18n.t('trials.caption.results.knowledge_chunk.weight'),
        ];
        const weightKey: any = this.$i18n.t('trials.caption.results.knowledge_chunk.weight');
        this.defaultSortPos[weightKey] = true;
        this.defaultSortNeg[weightKey] = false;

        // 表示に必要なカラムのデータだけ抽出
        this.tableDataPos = this.kcList.posObjects.map(obj => {
            return [obj.chunk, obj.weight];
        });

        this.tableDataNeg = this.kcList.negObjects.map(obj => {
            return [obj.chunk, obj.weight];
        });

        // header追加
        this.tableDataPos.unshift(showHeaders);
        this.tableDataNeg.unshift(showHeaders);
    }
}
